/* eslint-disable @typescript-eslint/no-unsafe-return */
import { computed } from "vue";

import Find from "@/components/CasePanels/Find/Find.vue";
import Filters from "@/components/CasePanels/Filters.vue";
import RsFileList from "@/components/CasePanels/FileList.vue";
import ChecksandScores from "@/components/CasePanels/ChecksandScores/ChecksandScores.vue";
import Summary from "@/components/CasePanels/Summary/Summary.vue";
import AgreementPeriods from "@/components/CasePanels/AgreementPeriods.vue";
import AgentAssistant from "@/components/CasePanels/AgentAssistant.vue";
import VoiceFormRenderer from "@/components/CasePanels/VoiceFormRenderer/VoiceFormRenderer.vue";
import VoicePlayer from "@/components/CasePanels/VoicePlayer/VoicePlayer.vue";
import CaseDetails from "@/components/CasePanels/CaseDetails/CaseDetails.vue";
import CaseEvidence from "@/components/CasePanels/CaseEvidence/CaseEvidence.vue";
import SummaryNotes from "@/components/CasePanels/SummaryNotes/SummaryNotes.vue";
import RecordDetails from "@/components/CasePanels/RecordDetails/RecordDetails.vue";
import RecordView from "@/components/CasePanels/RecordView/RecordView.vue";
import PopoutRecordView from "@/components/CasePanels/RecordView/PopoutRecordView.vue";

import { config } from "@/config";
import { useBrandStore } from "@/stores/brand-store";
import { useMetadataStore } from "@/stores/metadata-store";

import type { PanelConfigState } from "@/models/BrandViewerConfig";
import type { InjectedCaseRouteParams } from "@/hooks/use-case-route-params";

export function useAllCasePanels() {
  const brandStore = useBrandStore();

  const allPanels = computed<PanelMetadata>(() => {
    const panelsConfig = brandStore.panelsConfig;
    const DEFAULT_CONFIG: typeof panelsConfig = {
      agreementPeriods: { state: "conditional", order: panelsConfig.agreementPeriods?.order },
      details: { state: "conditional", order: panelsConfig.details?.order },
      agentAssistant: { state: "hidden", order: panelsConfig.agentAssistant?.order },
    };

    function isPanelEnabled(type: PanelType, state: PanelConfigState | undefined) {
      if (state == null) return true;

      if (state === "conditional") {
        switch (type) {
          case "filesList":
            return useMetadataStore().counts.totalFileCount > 1;
          case "agreementPeriods":
            return !!brandStore.viewerConfig.timeline?.general?.useUpper;
          case "agentAssistant":
            return config.agent != null;
          default:
            return true;
        }
      }

      return state !== "hidden";
    }

    function getPanelConfig(type: PanelType) {
      const panelConfig = panelsConfig[type];
      const fallbackConfig = DEFAULT_CONFIG[type];

      return {
        enabled: isPanelEnabled(type, panelConfig?.state ?? fallbackConfig?.state),
        defaultOpen: (panelConfig?.state ?? fallbackConfig?.state) !== "closed",
        order: panelConfig?.order ?? fallbackConfig?.order,
      };
    }

    return {
      agreementPeriods: { title: "Agreement Periods", ...getPanelConfig("agreementPeriods") },
      recordView: { title: "Record View", ...getPanelConfig("recordView"), popoutEnabled: true },
      evidences: { title: "Bookmarks", ...getPanelConfig("evidences") },
      filesList: { title: "Record List", ...getPanelConfig("filesList") },
      filters: { title: "Record Filters", ...getPanelConfig("filters") },
      recordDetails: { title: "Record Details", ...getPanelConfig("recordDetails") },
      recordSummary: { title: "Summary Themes", ...getPanelConfig("recordSummary") },
      voiceForm: { title: "Form", ...getPanelConfig("voiceForm") },
      voicePlayer: { title: "Audio Player", ...getPanelConfig("voicePlayer") },
      summaryNotes: { title: "Summary Notes", ...getPanelConfig("summaryNotes") },
      find: { title: "Find", ...getPanelConfig("find") },
      details: { title: "Case Details", ...getPanelConfig("details") },
      checks: { title: "Checks", ...getPanelConfig("checks") },
      agentAssistant: { title: "Agent Assistant", ...getPanelConfig("agentAssistant") },
    };
  });

  return allPanels;
}

export function getCasePanelComponent(panelType: PanelType, isPopout: boolean) {
  if (isPopout) {
    switch (panelType) {
      case "recordView":
        return PopoutRecordView;
    }
    return;
  }

  switch (panelType) {
    case "agreementPeriods":
      return AgreementPeriods;
    case "evidences":
      return CaseEvidence;
    case "filesList":
      return RsFileList;
    case "filters":
      return Filters;
    case "recordView":
      return RecordView;
    case "recordDetails":
      return RecordDetails;
    case "recordSummary":
      return Summary;
    case "voiceForm":
      return VoiceFormRenderer;
    case "voicePlayer":
      return VoicePlayer;
    case "summaryNotes":
      return SummaryNotes;
    case "find":
      return Find;
    case "details":
      return CaseDetails;
    case "checks":
      return ChecksandScores;
    case "agentAssistant":
      return AgentAssistant;
    default:
      panelType satisfies never;
      return undefined;
  }
}

export type PanelMetadata = Record<
  PanelType,
  { title: string; enabled?: boolean; defaultOpen?: boolean; popoutEnabled?: boolean; order?: number }
>;

export interface DockviewPanelComponentProps<IsPopout extends boolean> {
  dockview?: {
    window: Window;
    isPopout: IsPopout;
    isVisible: boolean;
    size: Record<"width" | "height", number>;
    params: PanelProps<IsPopout>;
  };
}

// export type DockviewPanelComponentEmits = UnionToIntersection<
//   EmitObjectToOverload<{
//     openPopout: IsPopout extends false ? (fromParams: Pick<CaseRouteParams, OptionalPopoutRouteParams>) => void : never;
//     switchPopoutFileToMain: IsPopout extends true
//       ? (toParams: Pick<CaseRouteParams, OptionalPopoutRouteParams>) => void
//       : never;
//   }>
// >;

export type DockviewPanelComponentEmits = {
  panelPopoutAction: [toParams: Pick<InjectedCaseRouteParams, OptionalPopoutRouteParams>];
};

export type PanelProps<IsPopout extends boolean> = { panelType: PanelType } & (IsPopout extends true
  ? {
      popoutRouteParams: Required<Pick<InjectedCaseRouteParams, RequiredPopoutRouteParams>> &
        Pick<InjectedCaseRouteParams, OptionalPopoutRouteParams>;
    }
  : { popoutRouteParams?: never });

export type PanelType =
  | "filters"
  | "recordSummary"
  | "evidences"
  | "recordDetails"
  | "filesList"
  | "agreementPeriods"
  | "voiceForm"
  | "voicePlayer"
  | "summaryNotes"
  | "find"
  | "details"
  | "recordView"
  | "checks"
  | "agentAssistant";

export type OptionalPopoutRouteParams = "filepage" | "time";
export type RequiredPopoutRouteParams = "client" | "brand" | "casename" | "casefile";
